import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import { TextFieldProps } from '@material-ui/core/TextField';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import i18n from 'i18next';
import { snakeCase } from 'snake-case';

import { addOrUpdate } from '../../redux/modelsInfo/modelsInfoSlice';
import { ReduxState } from '../../redux/types';
import api from '../../utils/api';
import { useDialog } from '../../utils/dialog';
import { StyledTestField } from './styled';

/*
 * This component wrap the Input component, provided by Material-UI.
 * It implements some basic logis common to all the inputs in the application.
 */

export type Props = TextFieldProps & {
    name: string;
    data: any;
    handleAttributeChange?: (name: string, value: any) => void;
    handleKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    partialTranslationKey?: string;
    errorIfEmpty?: boolean
    booleanValues?: boolean;
    flex?: number | string;
    helpText?: string;
    translationKey?: string;
};

const CustomInput = (props: Props) => {
    const { name, data, handleAttributeChange, handleKeyDown, partialTranslationKey, errorIfEmpty, booleanValues, helpText, translationKey, ...otherProps} = props;
    const { select, multiline, type, disabled, label, SelectProps } = otherProps;

    const { t } = useTranslation();

    const modelsInfo = useSelector((state: ReduxState) => state.modelsInfo);
    const dispatch = useDispatch();

    const [openDialog] = useDialog();

    const inputRef = useRef<HTMLDivElement>(null);
    const [hasFocus, setHasFocus] = useState(false);

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
        return handleAttributeChange && handleAttributeChange(name, e.target.value);
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (handleKeyDown && !select && !multiline) {
            handleKeyDown(e);
        }
    };

    const capitalizeFirstLetter = (str: string) => {
        if (!str || str.length < 1) return str;
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const realTranslationKey = translationKey ?? (partialTranslationKey + '.' + snakeCase(name));
    const realLabel = label ?? (partialTranslationKey ? capitalizeFirstLetter(t(realTranslationKey)) : name);

    const helpTextFromTranslation = i18n.exists(realTranslationKey + '_help') ? t(realTranslationKey + '_help') : null;
    const realHelpText = helpText ?? helpTextFromTranslation;

    // nested key names are passed as strings in the format 'a.b.c.d...', the next line manage this case and get the value of the input
    const realValue = name.split('.').reduce((value, entry) => value ? value[entry] : undefined, data);

    // if real value is true or false, use 1 and 0 as values, as required by Material-UI select inputs
    const booleanValue = (realValue === true ? '1' : ((realValue === false || realValue === undefined) ? '0' : realValue));

    // check if the model attribute is required
    let isAttributeRequired = false;
    const splitTranslationKey = realTranslationKey.split('.');

    if (splitTranslationKey.length >=4 && splitTranslationKey[0] === 'models' && splitTranslationKey[2] === 'attributes') {
        const modelName = splitTranslationKey[1];
        const attributeName = splitTranslationKey[3];

        // get the model info from redux state
        const thisModelInfo = modelsInfo.find(model => model.name === modelName);

        if (!thisModelInfo) {
            // if the redux state doesn't contain this model info, fetch them from the API, and save them in redux
            dispatch(addOrUpdate({ name: modelName, requiredAttributes: [] }));

            api.request('/models/' + splitTranslationKey[1] + '/info').then(res => {
                dispatch(addOrUpdate({
                    name: modelName,
                    requiredAttributes: res.requiredAttributes,
                    translatableAttributes: res.translatableAttributes
                }));
            });
        }
        else {
            // otherwise check if the attribute is required
            isAttributeRequired = thisModelInfo.requiredAttributes.some(attribute => attribute === attributeName);
        }
    }

    const defaultProps: TextFieldProps = {
        ref: inputRef,
        value: (select && realValue === 0) ? '' : (booleanValues ? booleanValue : (realValue ?? '')),
        margin: 'dense',
        variant: 'outlined',
        color: 'primary',
        fullWidth: true,
        onBlur: () => setHasFocus(false),
        onFocus: () => setHasFocus(true),
        label: realLabel,
        onChange,
        onKeyDown,
        // Material-UI number inputs have a glitch when the value is programmatically changed.
        // In this app this is caused when component renders before API data is loaded.
        // The next line solve this, by re-rendering the number inputs every times the id of the entity changes.
        ...(type === 'number' && { key: data.id ?? '' }),
        ...(errorIfEmpty && { error: (!realValue || realValue.length === 0 )}),
        ...(isAttributeRequired && { required: true })
    };

    const openHelpDialog = useCallback(() => {
        openDialog({
            title: t('dialogs.input_help.title'),
            message: realHelpText ?? '',
            keepHtmlFormatting: true
        });
    }, [openDialog, realHelpText, t]);

    const helpButtonProps = realHelpText && realHelpText.length > 0 && {
        // this props are appended if the help text is set
        InputProps: {
            endAdornment: (
                <InputAdornment position='end' >
                    <IconButton size='small' onClick={openHelpDialog}>
                        <HelpOutlineIcon />
                    </IconButton>
                </InputAdornment>
            )
        },
        SelectProps: {
            ...SelectProps,
            IconComponent: () => null
        }
    };

    const finalProps = {...defaultProps, ...otherProps, ...helpButtonProps};

    useEffect(() => {
        // When an input is disabled and re-enabled, it lose the focus.
        // This effect give the focus back to the last focused input, when re-enabled.
        if (!disabled && hasFocus && inputRef.current && !select) {
            inputRef.current.getElementsByTagName('input')[0]?.focus();
        }
    }, [disabled, hasFocus, inputRef, select]);

    return (
        <StyledTestField {...finalProps} />
    );
};

const compareProps = (oldProps: any, newProps: any) => {
    // return true if the old and new props should be treated as the same

    const name = newProps.name;
    const keys = Object.keys(newProps);

    if (keys.some(key => key !== 'data' && oldProps[key] !== newProps[key])) {
        // if a prop (except data) is not equal in old and new props, props are differents
        return false;
    }

    const realValueOld = name.split('.').reduce((value:any, entry:any) => value ? value[entry] : undefined, oldProps.data);
    const realValueNew = name.split('.').reduce((value:any, entry:any) => value ? value[entry] : undefined, newProps.data);

    if (realValueOld !== realValueNew) {
        // if the current input value is different, props are differents
        return false;
    }

    // Otherwise, treat props as if they're the same.
    // We don't care about the other attributes in data, because they don't interact with this component instance.
    return true;
};

// memo is used to prevent useless re-render, that could cause performance issues
export default memo(CustomInput, compareProps);
